import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Shield, TrendingUp, TrendingDown, Users, ArrowRight, ArrowUpRight,
  CheckCircle2, BarChart3, Clock3, ExternalLink, Lock, Globe,
  Newspaper, ScanSearch, Gauge, Target, MessageCircle, FileText,
} from 'lucide-react';
import { tierColors } from '../../utils/credibilityTiers';
import { GUARDRAIL_ORDER, GUARDRAIL_INFO, formatPriceCeiling, translateGuardrailLabel } from '../../utils/groupGuardrailTiers';
import PublicNav from '../../components/publicNav/PublicNav';
// Real app components, imported directly rather than hand-approximated --
// both are pure presentational (props only, no redux/API deps), so they're
// safe to render on an anonymous marketing page. See the memory note
// "feedback_landing_page_component_reuse" for why this matters: two prior
// rounds of hand-rolled lookalikes on this page drifted from the real UI.
import CredibilityChip from '../../components/Credibility/CredibilityChip';
import GroupCredibilityChip from '../../pages/Groups/groupPageComponents/GroupCredibilityChip';

// Real credibility tier -> guardrail state, mirrors monetization_guardrails.py
// exactly -- same mapping the Pricing page itself uses, so this teaser and
// the full breakdown never drift apart.
const CREDIBILITY_TIER_FOR_STATE = { sandbox: 'Beginner', guarded: 'Advanced', extended: 'Elite', unrestricted: 'Top Emperor' };

const SURVEY_URL = 'https://qualtricsxmq4dghtjcn.qualtrics.com/jfe/form/SV_cOTOm0og60VRZBQ';

// Reveal-on-scroll: fires once when a section first enters the viewport, then
// stays revealed. A below-the-fold marketing page needs real scroll-triggered
// motion; nothing here re-hides on scroll-away, which would be distracting
// rather than purposeful.
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const Reveal = ({ as: Tag = 'div', delay = 0, className = '', style = {}, children }) => {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      className={`reveal ${inView ? 'show' : ''} ${className}`}
      style={{ transitionDelay: `${delay}s`, ...style }}
    >
      {children}
    </Tag>
  );
};

// Section titles carry a highlighted keyword (the "titleAccent" i18n key,
// substring-matched against "title") so a scanning eye has one bright anchor
// per heading instead of a uniform wall of white text -- the same pattern
// the hero's rotating phrase already uses, applied to every section title.
const AccentTitle = ({ title, accent, style }) => {
  if (!accent || !title.includes(accent)) return <h2 style={style}>{title}</h2>;
  const idx = title.indexOf(accent);
  return (
    <h2 style={style}>
      {title.slice(0, idx)}
      <span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        {accent}
      </span>
      {title.slice(idx + accent.length)}
    </h2>
  );
};

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(true);

  // PublicNav's cross-page anchor links (e.g. from /pricing back to
  // /#credibility) land here as a location.hash on a fresh mount, after
  // the sections below have already rendered -- scroll to it once.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    return () => clearTimeout(t);
  }, [location.hash]);

  const ACCENT_PHRASES = t('home.hero.headlineAccentWords', { returnObjects: true });
  const TRUST_STRIP = t('home.trustStrip', { returnObjects: true });
  const HOW_STEPS = t('home.howItWorks.steps', { returnObjects: true });
  const FORMULA = t('home.credibility.formula', { returnObjects: true });
  const TIERS = t('home.credibility.tiers', { returnObjects: true });
  const LEADERBOARD = t('home.gamification.leaderboard', { returnObjects: true });
  const legacyPricingTiers = useSelector((s) => s.featureFlags.flags.legacyPricingTiers);
  const PRICING_TIERS = legacyPricingTiers ? t('home.pricingTeaser.tiers', { returnObjects: true }) : [];

  useEffect(() => {
    const tm = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(tm);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseVisible(false);
      setTimeout(() => {
        setPhraseIdx((i) => (i + 1) % ACCENT_PHRASES.length);
        setPhraseVisible(true);
      }, 350);
    }, 2800);
    return () => clearInterval(interval);
  }, [ACCENT_PHRASES.length]);

  const stepIcons = [ScanSearch, Gauge, TrendingUp];
  const formulaIcons = [Target, BarChart3, Shield, Clock3, MessageCircle];
  const sectionTitleStyle = {
    fontFamily: "'Bricolage Grotesque', sans-serif",
    fontSize: 'clamp(2.3rem, 4.6vw, 3.4rem)',
    fontWeight: 800,
    color: 'var(--hp-heading)',
    marginTop: 10,
    marginBottom: 16,
    letterSpacing: '-0.025em',
    lineHeight: 1.08,
  };

  return (
    <div style={{ minHeight: '100vh', width: '100%', background: 'var(--hp-bg)', fontFamily: "'Inter', 'Helvetica Neue', sans-serif", overflowX: 'hidden' }}>
      <Helmet>
        <title>Stocker Beta: AI Credibility & Market Insight</title>
        <meta
          name="description"
          content="Stocker Beta is an early-access AI platform ranking financial insights by credibility and sentiment across filings and predictions."
        />
        <link rel="canonical" href="https://stocker-beta.com/" />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Bricolage+Grotesque:opsz,wght@12..96,700;12..96,800&display=swap');

        * { box-sizing: border-box; }

        /* Light/dark tokens for this page's inline-style-driven content.
           :root holds light defaults (Tailwind's darkMode:"class" base);
           .dark (toggled on <html> by ThemeContext) overrides with the
           original dark-only values this page shipped with. Pure CSS --
           no JS/theme-detection logic added. */
        :root {
          --hp-bg: #ffffff;
          --hp-heading: #0f172a;
          --hp-eyebrow: #7c3aed;
          --hp-body-faint: #64748b;
          --hp-body-loud: #334155;
          --hp-tag-blue-text: #2563eb;
          --hp-decay-text: #dc2626;
          --hp-badge-emerald: #059669;
          --hp-published-news: #2563eb;
          --hp-stat-bg: rgba(124,58,237,0.08);
          --hp-stat-text: #6d28d9;
          --hp-stat-border: rgba(124,58,237,0.25);
          --hp-cta-secondary-bg: rgba(0,0,0,0.03);
          --hp-cta-secondary-text: #1e293b;
          --hp-cta-secondary-border: rgba(0,0,0,0.12);
          --hp-cta-secondary-hover-bg: rgba(0,0,0,0.06);
          --hp-cta-secondary-hover-border: rgba(0,0,0,0.2);
          --hp-cta-ghost: #475569;
          --hp-cta-ghost-hover: #1e293b;
          --hp-nav-link-hover: #1e293b;
          --hp-card-bg: rgba(0,0,0,0.03);
          --hp-card-border: rgba(0,0,0,0.1);
          --hp-card-border-hover: rgba(0,0,0,0.16);
          --hp-card-bg-hover: rgba(0,0,0,0.05);
          --hp-divider: rgba(0,0,0,0.08);
          --hp-num-ghost: rgba(0,0,0,0.08);
          --hp-formula-border: rgba(0,0,0,0.08);
          --hp-formula-icon-bg: rgba(0,0,0,0.04);
          --hp-formula-icon-border: rgba(0,0,0,0.1);
        }
        .dark {
          --hp-bg: #09090f;
          --hp-heading: #ffffff;
          --hp-eyebrow: #a78bfa;
          --hp-body-faint: #94a3b8;
          --hp-body-loud: #cbd5e1;
          --hp-tag-blue-text: #93c5fd;
          --hp-decay-text: #fca5a5;
          --hp-badge-emerald: #6ee7b7;
          --hp-published-news: #60a5fa;
          --hp-stat-bg: rgba(167,139,250,0.1);
          --hp-stat-text: #c4b5fd;
          --hp-stat-border: rgba(167,139,250,0.2);
          --hp-cta-secondary-bg: rgba(255,255,255,0.04);
          --hp-cta-secondary-text: #e2e8f0;
          --hp-cta-secondary-border: rgba(255,255,255,0.1);
          --hp-cta-secondary-hover-bg: rgba(255,255,255,0.07);
          --hp-cta-secondary-hover-border: rgba(255,255,255,0.18);
          --hp-cta-ghost: #94a3b8;
          --hp-cta-ghost-hover: #e2e8f0;
          --hp-nav-link-hover: #cbd5e1;
          --hp-card-bg: rgba(255,255,255,0.03);
          --hp-card-border: rgba(255,255,255,0.08);
          --hp-card-border-hover: rgba(255,255,255,0.14);
          --hp-card-bg-hover: rgba(255,255,255,0.045);
          --hp-divider: rgba(255,255,255,0.06);
          --hp-num-ghost: rgba(255,255,255,0.08);
          --hp-formula-border: rgba(255,255,255,0.05);
          --hp-formula-icon-bg: rgba(255,255,255,0.04);
          --hp-formula-icon-border: rgba(255,255,255,0.08);
        }

        .reveal {
          opacity: 0;
          transform: translateY(22px);
          transition: opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1);
        }
        .reveal.show { opacity: 1; transform: none; }

        .phrase-swap {
          display: inline-block;
          opacity: 1;
          transform: translateY(0);
          transition: opacity 0.3s ease, transform 0.3s ease;
          background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #60a5fa 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .phrase-swap.hide { opacity: 0; transform: translateY(-8px); }

        .eyebrow {
          display: inline-flex;
          align-items: center;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--hp-eyebrow);
        }

        .stat-label {
          display: inline-flex;
          align-items: center;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.03em;
          padding: 3px 8px;
          border-radius: 6px;
          background: var(--hp-stat-bg);
          color: var(--hp-stat-text);
          border: 1px solid var(--hp-stat-border);
        }

        .tag {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.02em;
          padding: 3px 8px;
          border-radius: 6px;
          border-width: 1px;
          border-style: solid;
        }

        .cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #7c3aed;
          color: #fff;
          font-size: 14.5px;
          font-weight: 600;
          padding: 14px 26px;
          border-radius: 9px;
          text-decoration: none;
          letter-spacing: 0.01em;
          transition: background 0.15s ease, transform 0.15s ease;
          border: 1px solid rgba(255,255,255,0.08);
          font-family: inherit;
          cursor: pointer;
        }
        .cta-primary:hover { background: #6d28d9; transform: translateY(-1px); }
        .cta-primary:active { transform: none; }

        .cta-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--hp-cta-secondary-bg);
          color: var(--hp-cta-secondary-text);
          font-size: 14.5px;
          font-weight: 500;
          padding: 14px 24px;
          border-radius: 9px;
          text-decoration: none;
          transition: background 0.15s ease, border-color 0.15s ease;
          border: 1px solid var(--hp-cta-secondary-border);
          font-family: inherit;
          cursor: pointer;
        }
        .cta-secondary:hover { background: var(--hp-cta-secondary-hover-bg); border-color: var(--hp-cta-secondary-hover-border); }

        .cta-ghost {
          color: var(--hp-cta-ghost);
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.15s ease;
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
        }
        .cta-ghost:hover { color: var(--hp-cta-ghost-hover); }

        .nav-link {
          color: #64748b;
          font-size: 13.5px;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.15s ease;
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
        }
        .nav-link:hover { color: var(--hp-nav-link-hover); }

        .card {
          background: var(--hp-card-bg);
          border: 1px solid var(--hp-card-border);
          border-radius: 14px;
          transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
        }
        .card:hover { border-color: var(--hp-card-border-hover); background: var(--hp-card-bg-hover); transform: translateY(-2px); }

        .section-pad { max-width: 1080px; margin: 0 auto; padding: 100px 24px; }
        .divider { height: 1px; background: var(--hp-divider); }

        .noise-layer {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          background-size: 256px 256px;
          opacity: 0.015;
        }

        .content { position: relative; z-index: 1; }

        @media (max-width: 860px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .mockup-wrap { margin-top: 48px; }
          .cred-grid { grid-template-columns: 1fr !important; }
          .steps-row { flex-direction: column !important; }
          .step-arrow { display: none !important; }
          .pricing-grid { grid-template-columns: 1fr !important; }
          .footer-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
        }
      `}</style>

      <div className="noise-layer" />
      <div style={{
        position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
        width: 1000, height: 520,
        background: 'radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.15) 0%, transparent 65%)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      <div className="content">

        {/* ============ NAV (shared across every public page) ============ */}
        <PublicNav />

        {/* ============ HERO ============ */}
        <section style={{ maxWidth: 1160, margin: '0 auto', padding: '88px 24px 72px' }}>
          <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: 56, alignItems: 'center' }}>

            <div>
              <div className={`reveal ${mounted ? 'show' : ''}`} style={{ marginBottom: 28 }}>
                <span className="eyebrow">{t('home.hero.eyebrow')}</span>
              </div>

              <h1
                className={`reveal ${mounted ? 'show' : ''}`}
                style={{
                  transitionDelay: '0.06s',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontSize: 'clamp(2.7rem, 5.4vw, 4.4rem)',
                  fontWeight: 800,
                  color: 'var(--hp-heading)',
                  lineHeight: 1.03,
                  letterSpacing: '-0.03em',
                  marginBottom: 28,
                }}
              >
                {t('home.hero.headlineLine1')}<br />
                {/* min-height reserves room for the longest phrase wrapping to
                    2 lines, in em units so it scales with the h1's own clamp()
                    font-size -- without this, phrases that wrap differently
                    (1 line vs 2) shift every element below on each rotation. */}
                <span style={{ display: 'block', minHeight: '2.12em' }}>
                  <span className={`phrase-swap ${phraseVisible ? '' : 'hide'}`}>
                    {ACCENT_PHRASES[phraseIdx]}
                  </span>
                </span>
              </h1>

              <p
                className={`reveal ${mounted ? 'show' : ''}`}
                style={{ transitionDelay: '0.12s', fontSize: '1.08rem', color: 'var(--hp-body-faint)', lineHeight: 1.75, maxWidth: 540, marginBottom: 40 }}
              >
                {t('home.hero.subheading')}
              </p>

              <div className={`reveal ${mounted ? 'show' : ''}`} style={{ transitionDelay: '0.18s', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <button className="cta-primary" onClick={() => navigate('/register')}>
                  {t('home.hero.ctaPrimary')} <ArrowRight size={15} />
                </button>
                <button className="cta-secondary" onClick={() => navigate('/login')}>
                  {t('home.hero.ctaSecondary')}
                </button>
              </div>
            </div>

            {/* Product mockup: reuses the real CredibilityChip component
                directly, and exact-copies EvidenceItem / the Bull-Bear vote
                buttons / the vote-consensus bar from Post.jsx (those three
                are unexported locals entangled with that file's redux/vote
                mutation logic, so they're reproduced class-for-class rather
                than imported -- see the [COMPONENT MAPPING AUDIT] this was
                built from, and the "feedback_landing_page_component_reuse"
                memory). Not an abstract illustration -- the mockup IS the
                real product's UI, not an approximation of it. */}
            <div className={`reveal mockup-wrap ${mounted ? 'show' : ''}`} style={{ transitionDelay: '0.22s' }}>
              <div className="card" style={{ padding: 22, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="tag" style={{ background: 'rgba(96,165,250,0.1)', borderColor: 'rgba(96,165,250,0.28)', color: 'var(--hp-tag-blue-text)' }}>
                      {t('home.hero.mockup.ticker')}
                    </span>
                    <span style={{ fontSize: 13, color: '#64748b' }}>{t('home.hero.mockup.author')}</span>
                  </div>
                  <CredibilityChip cred={{ score: 842, tier: 'Elite', accuracyPct: 71 }} size="sm" />
                </div>

                <p style={{ fontSize: 13.5, color: 'var(--hp-body-loud)', lineHeight: 1.6, marginBottom: 16 }}>
                  {t('home.hero.mockup.body')}
                </p>

                {/* Exact copy of Post.jsx's EvidenceItem, sec_filing variant */}
                <a
                  href="#evidence"
                  onClick={(e) => e.preventDefault()}
                  className="flex items-start gap-2 sm:gap-3 p-3 bg-gradient-to-r from-purple-100 to-purple-50 border-purple-300 text-purple-700 dark:from-purple-600/20 dark:to-purple-500/10 dark:border-purple-500/30 dark:text-purple-400 border rounded-xl transition-all duration-200 hover:scale-[1.01] hover:shadow-lg group"
                  style={{ marginBottom: 16 }}
                >
                  <FileText className="w-4 h-4 flex-shrink-0" style={{ marginTop: 4 }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold uppercase tracking-wide opacity-75">{t('home.hero.mockup.evidenceType')}</span>
                      <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 break-words">{t('home.hero.mockup.evidenceTitle')}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-2 italic border-l-2 border-current pl-2 line-clamp-2 break-words">
                      "{t('home.hero.mockup.evidenceNote')}"
                    </p>
                  </div>
                </a>

                {/* Exact copy of Post.jsx's Bull/Bear vote button pair, Bull active */}
                <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                  <button disabled style={{ cursor: 'default' }} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium border-2 transition-all duration-200 select-none bg-gradient-to-r from-emerald-100 to-emerald-50 text-emerald-700 border-emerald-400 dark:from-emerald-600/30 dark:to-emerald-500/30 dark:text-emerald-300 dark:border-emerald-500/50 shadow-lg shadow-emerald-500/20 flex-1 justify-center">
                    <TrendingUp size={16} className="flex-shrink-0" />
                    <span>{t('home.hero.mockup.bullLabel')}</span>
                  </button>
                  <button disabled style={{ cursor: 'default' }} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium border-2 transition-all duration-200 select-none bg-gray-100 text-gray-500 border-gray-300 dark:bg-gray-700/30 dark:text-gray-400 dark:border-gray-600/50 flex-1 justify-center">
                    <TrendingDown size={16} className="flex-shrink-0" />
                    <span>{t('home.hero.mockup.bearLabel')}</span>
                  </button>
                </div>

                {/* Exact copy of Post.jsx's Vote Consensus block */}
                <div className="p-3 sm:p-4 bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-200 dark:from-gray-800/50 dark:to-gray-700/50 rounded-xl dark:border-gray-700/50">
                  <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 mb-2 gap-2">
                    <span className="flex items-center gap-1.5 font-medium whitespace-nowrap">
                      <TrendingUp size={14} className="text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
                      <span>{t('home.hero.mockup.bullLabel')}</span>
                      <span className="w-6 text-center inline-block">{t('home.hero.mockup.bullCount')}</span>
                    </span>
                    <span className="font-bold text-xs sm:text-sm text-emerald-700 dark:text-emerald-400 text-center">
                      {t('home.hero.mockup.consensusLabel')} ({t('home.hero.mockup.confidence')}%)
                    </span>
                    <span className="flex items-center gap-1.5 font-medium whitespace-nowrap">
                      <TrendingDown size={14} className="text-red-600 dark:text-red-400 flex-shrink-0" />
                      <span>{t('home.hero.mockup.bearLabel')}</span>
                      <span className="w-6 text-center inline-block">{t('home.hero.mockup.bearCount')}</span>
                    </span>
                  </div>
                  <div className="h-2.5 bg-gray-200 dark:bg-gray-900 rounded-full overflow-hidden flex shadow-inner">
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: '72%' }} />
                    <div className="bg-gradient-to-r from-red-500 to-red-400" style={{ width: '28%' }} />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                    <span>{t('home.hero.mockup.bullPct')}% {t('home.hero.mockup.bullLabel')}</span>
                    <span>{t('home.hero.mockup.bearPct')}% {t('home.hero.mockup.bearLabel')}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* ============ TRUST STRIP ============ */}
        <div className="divider" />
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '28px 24px' }}>
          <Reveal>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 36px', justifyContent: 'center' }}>
              {TRUST_STRIP.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#64748b' }}>
                  <CheckCircle2 size={13} color="#475569" />
                  {item}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="divider" />

        {/* ============ HOW IT WORKS ============ */}
        <section id="how-it-works" className="section-pad">
          <Reveal><span className="eyebrow">{t('home.howItWorks.eyebrow')}</span></Reveal>
          <Reveal delay={0.05}>
            <AccentTitle title={t('home.howItWorks.title')} accent={t('home.howItWorks.titleAccent')} style={{ ...sectionTitleStyle, maxWidth: 640 }} />
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontSize: 14.5, color: '#64748b', lineHeight: 1.7, maxWidth: 620, marginBottom: 52 }}>
              {t('home.howItWorks.subtitle')}
            </p>
          </Reveal>

          <div className="steps-row" style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
            {HOW_STEPS.map((step, i) => {
              const Icon = stepIcons[i];
              return (
                <React.Fragment key={i}>
                  <Reveal delay={0.12 + i * 0.08} style={{ flex: 1 }}>
                    <div className="card" style={{ padding: 26, height: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(124,58,237,0.14)', border: '1px solid rgba(124,58,237,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon size={18} color="#a78bfa" />
                        </div>
                        <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--hp-num-ghost)', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                          0{i + 1}
                        </span>
                      </div>
                      <p style={{ fontSize: 17, fontWeight: 700, color: 'var(--hp-heading)', marginBottom: 10, letterSpacing: '-0.01em' }}>{step.title}</p>
                      <p style={{ fontSize: 13.5, color: '#64748b', lineHeight: 1.65, marginBottom: 14 }}>{step.desc}</p>
                      <span className="stat-label">{step.stat}</span>
                    </div>
                  </Reveal>
                  {i < HOW_STEPS.length - 1 && (
                    <div className="step-arrow" style={{ display: 'flex', alignItems: 'center', color: '#334155' }}>
                      <ArrowRight size={18} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </section>

        <div className="divider" />

        {/* ============ CREDIBILITY ============ */}
        <section id="credibility" className="section-pad">
          <Reveal><span className="eyebrow">{t('home.credibility.eyebrow')}</span></Reveal>
          <Reveal delay={0.05}>
            <AccentTitle title={t('home.credibility.title')} accent={t('home.credibility.titleAccent')} style={sectionTitleStyle} />
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontSize: 14.5, color: '#64748b', lineHeight: 1.7, marginBottom: 48, maxWidth: 640 }}>
              {t('home.credibility.subtitle')}
            </p>
          </Reveal>

          <div className="cred-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 0.85fr', gap: 64 }}>

            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {FORMULA.map((f, i) => {
                  const Icon = formulaIcons[i];
                  return (
                    <Reveal key={i} delay={0.12 + i * 0.05}>
                      <div style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: i < FORMULA.length - 1 ? '1px solid var(--hp-formula-border)' : 'none' }}>
                        <div style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 9, background: 'var(--hp-formula-icon-bg)', border: '1px solid var(--hp-formula-icon-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon size={15} color="#a78bfa" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                            <p style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hp-body-loud)' }}>{f.title}</p>
                            <span className="stat-label">{f.stat}</span>
                          </div>
                          <p style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.6 }}>{f.desc}</p>
                        </div>
                      </div>
                    </Reveal>
                  );
                })}
              </div>

              <Reveal delay={0.35}>
                <p style={{ fontSize: 12.5, color: 'var(--hp-decay-text)', lineHeight: 1.7, marginTop: 22, paddingLeft: 14, borderLeft: '2px solid rgba(248,113,113,0.35)' }}>
                  {t('home.credibility.decayNote')}
                </p>
              </Reveal>

              <Reveal delay={0.4}>
                <p style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.7, marginTop: 16, paddingLeft: 14, borderLeft: '2px solid rgba(167,139,250,0.3)' }}>
                  {t('home.credibility.disputeNote')}
                </p>
              </Reveal>
            </div>

            <div>
              <Reveal delay={0.15}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 18 }}>
                  {t('home.credibility.tiersTitle')}
                </p>
              </Reveal>
              <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: 10 }}>
                {TIERS.map((tier, i) => {
                  const c = tierColors(tier.name);
                  const isTop = i === TIERS.length - 1;
                  return (
                    <Reveal key={tier.name} delay={0.2 + i * 0.06}>
                      <div className="card" style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, borderColor: isTop ? 'rgba(251,191,36,0.3)' : undefined }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span className={`tag ${c.bg} ${c.border} ${c.text}`}>{tier.name}</span>
                          <span style={{ fontSize: 12, color: '#475569' }}>{tier.range}</span>
                        </div>
                        <span style={{ fontSize: 12, color: '#64748b', textAlign: 'right', maxWidth: 180 }}>{tier.desc}</span>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
              <Reveal delay={0.55}>
                <p style={{ fontSize: 12, color: '#475569', marginTop: 18, lineHeight: 1.6 }}>
                  {t('home.credibility.noGaming')}
                </p>
              </Reveal>
            </div>

          </div>
        </section>

        <div className="divider" />

        {/* ============ GROUPS + PUBLISHED NEWS ============ */}
        <section id="groups" className="section-pad">
          <Reveal><span className="eyebrow">{t('home.groups.eyebrow')}</span></Reveal>
          <Reveal delay={0.05}>
            <AccentTitle title={t('home.groups.title')} accent={t('home.groups.titleAccent')} style={{ ...sectionTitleStyle, maxWidth: 620 }} />
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontSize: 14.5, color: '#64748b', lineHeight: 1.7, maxWidth: 600, marginBottom: 48 }}>
              {t('home.groups.subtitle')}
            </p>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }} className="cred-grid">

            <Reveal delay={0.16}>
              <div className="card" style={{ padding: 24, height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(124,58,237,0.14)', border: '1px solid rgba(124,58,237,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Users size={16} color="#a78bfa" />
                    </div>
                    <div>
                      <p style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hp-heading)' }}>{t('home.groups.groupCard.name')}</p>
                      <p style={{ fontSize: 11.5, color: '#64748b' }}>{t('home.groups.groupCard.members')}</p>
                    </div>
                  </div>
                  <GroupCredibilityChip credibility={{ average_credibility: 728.4, total_credibility: 45890, member_count: 63 }} />
                </div>
                <div className="divider" style={{ marginBottom: 16 }} />
                <p style={{ fontSize: 13, color: 'var(--hp-body-faint)', lineHeight: 1.6, marginBottom: 14 }}>
                  {t('home.groups.groupCard.description')}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                    {t('home.groups.groupCard.tickersLabel')}
                  </span>
                  {['NVDA', 'AMD', 'TSM', 'AVGO'].map((sym) => (
                    <span key={sym} className="tag" style={{ background: 'rgba(96,165,250,0.1)', borderColor: 'rgba(96,165,250,0.25)', color: 'var(--hp-tag-blue-text)' }}>${sym}</span>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.22}>
              <div className="card" style={{ padding: 24, height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(96,165,250,0.14)', border: '1px solid rgba(96,165,250,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Newspaper size={17} color="#60a5fa" />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--hp-published-news)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {t('home.groups.publishedNews.label')}
                  </span>
                </div>
                <AccentTitle
                  title={t('home.groups.publishedNews.title')}
                  accent={t('home.groups.publishedNews.titleAccent')}
                  style={{ fontSize: 19, fontWeight: 800, color: 'var(--hp-heading)', marginBottom: 10, fontFamily: "'Bricolage Grotesque', sans-serif", letterSpacing: '-0.01em' }}
                />
                <p style={{ fontSize: 13, color: 'var(--hp-body-faint)', lineHeight: 1.65, marginBottom: 16 }}>
                  {t('home.groups.publishedNews.desc')}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--hp-badge-emerald)' }}>
                  <CheckCircle2 size={12} />
                  {t('home.groups.publishedNews.badge')}
                </div>
              </div>
            </Reveal>

          </div>
        </section>

        <div className="divider" />

        {/* ============ GAMIFICATION / LEADERBOARD ============ */}
        <section className="section-pad">
          <Reveal><span className="eyebrow">{t('home.gamification.eyebrow')}</span></Reveal>
          <Reveal delay={0.05}>
            <AccentTitle title={t('home.gamification.title')} accent={t('home.gamification.titleAccent')} style={sectionTitleStyle} />
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontSize: 14.5, color: '#64748b', lineHeight: 1.7, maxWidth: 560, marginBottom: 40 }}>
              {t('home.gamification.subtitle')}
            </p>
          </Reveal>

          <Reveal delay={0.16}>
            <div className="card" style={{ padding: '8px 0', overflow: 'hidden' }}>
              <div style={{ padding: '10px 24px', fontSize: 11, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700 }}>
                {t('home.gamification.leaderboardNote')}
              </div>
              {/* Rank-badge treatment (gold/silver/bronze gradient circle,
                  trophy emoji for #1) and CredibilityChip are an exact copy
                  of LeaderboardPage.jsx's real rankBadgeClass()/LeaderboardRow --
                  that component itself isn't exported (entangled with FLIP-
                  animation refs and live redux data), so it's reproduced
                  class-for-class rather than imported. */}
              {LEADERBOARD.map((row, i) => {
                const rankBadgeClass =
                  i === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black'
                  : i === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-600 text-white'
                  : i === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white'
                  : 'bg-gray-600 text-gray-300';
                return (
                  <div key={i} className="flex items-center justify-between gap-3" style={{ padding: '14px 24px', borderTop: '1px solid var(--hp-divider)' }}>
                    <div className="flex items-center gap-4">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${rankBadgeClass}`}>
                        {i === 0 ? '🏆' : i + 1}
                      </div>
                      <CredibilityChip cred={{ score: row.score, tier: row.tier }} size="sm" showAccuracy={false} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
          <Reveal delay={0.24}>
            <p style={{ fontSize: 12, color: '#475569', marginTop: 14, lineHeight: 1.6 }}>
              {t('home.gamification.secondaryNote')}
            </p>
          </Reveal>
        </section>

        <div className="divider" />

        {/* ============ MONETIZATION TEASER ============ */}
        {/* Real group-monetization mechanism, not the retired SaaS-tier
            pricing model (see legacyPricingTiers below) -- same tier/price/
            take-rate data source as the Pricing page itself
            (groupGuardrailTiers.js / monetization_guardrails.py /
            take_rates.py), condensed to a teaser rather than repeating the
            full tier grid twice. */}
        <section className="section-pad">
          <Reveal><span className="eyebrow">{t('home.monetizationTeaser.eyebrow')}</span></Reveal>
          <Reveal delay={0.05}>
            <AccentTitle title={t('home.monetizationTeaser.title')} accent={t('home.monetizationTeaser.titleAccent')} style={sectionTitleStyle} />
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontSize: 14.5, color: '#64748b', lineHeight: 1.7, maxWidth: 620, marginBottom: 40 }}>
              {t('home.monetizationTeaser.subtitle')}
            </p>
          </Reveal>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 32 }}>
            {GUARDRAIL_ORDER.filter((s) => s !== 'sandbox').map((state, i) => {
              const credTier = CREDIBILITY_TIER_FOR_STATE[state];
              const info = GUARDRAIL_INFO[state];
              const colors = tierColors(credTier);
              return (
                <Reveal key={state} delay={0.14 + i * 0.06} style={{ flex: '1 1 220px', minWidth: 220 }}>
                  <div className="card" style={{ padding: 22, height: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.28)' }} className={colors.text}>
                        {translateGuardrailLabel(state, t)} <span style={{ color: '#64748b', fontWeight: 500 }}>· {credTier}</span>
                      </span>
                      {info.allowsPublicGroup ? <Globe size={14} color="#6ee7b7" /> : <Lock size={14} color="#64748b" />}
                    </div>
                    <p style={{ fontSize: 22, fontWeight: 800, color: 'var(--hp-heading)', marginBottom: 6, fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                      {formatPriceCeiling(info.priceCeilingCents, t)}
                    </p>
                    <p style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.6 }}>{info.minScore}+ {t('home.monetizationTeaser.scoreSuffix')}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.3}>
            <button className="cta-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => navigate('/pricing')}>
              {t('home.monetizationTeaser.viewAll')} <ArrowUpRight size={14} />
            </button>
          </Reveal>
        </section>

        {legacyPricingTiers && (
          <>
            <div className="divider" />
            <section className="section-pad">
              <Reveal><span className="eyebrow">{t('home.pricingTeaser.eyebrow')}</span></Reveal>
              <Reveal delay={0.05}>
                <AccentTitle title={t('home.pricingTeaser.title')} accent={t('home.pricingTeaser.titleAccent')} style={sectionTitleStyle} />
              </Reveal>
              <Reveal delay={0.1}>
                <p style={{ fontSize: 14.5, color: '#64748b', lineHeight: 1.7, maxWidth: 560, marginBottom: 40 }}>
                  {t('home.pricingTeaser.subtitle')}
                </p>
              </Reveal>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 32 }} className="pricing-grid">
                {PRICING_TIERS.map((tier, i) => (
                  <Reveal key={tier.name} delay={0.14 + i * 0.06}>
                    <div className="card" style={{ padding: 24, position: 'relative', borderColor: tier.badge ? 'rgba(124,58,237,0.4)' : undefined }}>
                      {tier.badge && (
                        <span className="tag" style={{ position: 'absolute', top: -10, left: 24, background: '#7c3aed', borderColor: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                          {tier.badge}
                        </span>
                      )}
                      <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--hp-body-loud)', marginBottom: 6 }}>{tier.name}</p>
                      <p style={{ fontSize: 24, fontWeight: 800, color: 'var(--hp-heading)', marginBottom: 10, fontFamily: "'Bricolage Grotesque', sans-serif" }}>{tier.price}</p>
                      <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>{tier.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={0.3}>
                <button className="cta-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => navigate('/pricing')}>
                  {t('home.pricingTeaser.viewAll')} <ArrowUpRight size={14} />
                </button>
              </Reveal>
            </section>
          </>
        )}

        {/* ============ FINAL CTA ============ */}
        <section style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 24px 100px' }}>
          <Reveal>
            <div className="card" style={{ padding: '56px 40px', textAlign: 'center', background: 'linear-gradient(180deg, rgba(124,58,237,0.08) 0%, rgba(255,255,255,0.02) 100%)' }}>
              <AccentTitle
                title={t('home.finalCta.title')}
                accent={t('home.finalCta.titleAccent')}
                style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 'clamp(1.7rem, 3.6vw, 2.4rem)', fontWeight: 800, color: 'var(--hp-heading)', marginBottom: 14, letterSpacing: '-0.02em' }}
              />
              <p style={{ fontSize: 14.5, color: '#64748b', marginBottom: 32 }}>
                {t('home.finalCta.subtitle')}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                <button className="cta-primary" onClick={() => navigate('/register')}>
                  {t('home.finalCta.ctaPrimary')} <ArrowRight size={15} />
                </button>
                <button className="cta-secondary" onClick={() => navigate('/login')}>
                  {t('home.finalCta.ctaSecondary')}
                </button>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ============ FOOTER ============ */}
        <footer style={{ borderTop: '1px solid var(--hp-divider)', padding: '56px 24px 32px' }}>
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 32, marginBottom: 40 }} className="footer-grid">
              <div>
                <img src="/StockerLogo.png" alt="Stocker" style={{ height: 26, marginBottom: 14 }} />
                <p style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6, maxWidth: 220 }}>
                  {t('home.footer.tagline')}
                </p>
              </div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--hp-body-loud)', marginBottom: 12 }}>{t('home.footer.productHeading')}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button className="nav-link" style={{ textAlign: 'left' }} onClick={() => navigate('/features')}>{t('home.footer.featuresLink')}</button>
                  <button className="nav-link" style={{ textAlign: 'left' }} onClick={() => navigate('/pricing')}>{t('home.footer.pricingLink')}</button>
                </div>
              </div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--hp-body-loud)', marginBottom: 12 }}>{t('home.footer.communityHeading')}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button className="nav-link" style={{ textAlign: 'left' }} onClick={() => navigate('/register')}>{t('home.footer.groupsLink')}</button>
                  <button className="nav-link" style={{ textAlign: 'left' }} onClick={() => navigate('/register')}>{t('home.footer.leaderboardLink')}</button>
                </div>
              </div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--hp-body-loud)', marginBottom: 12 }}>{t('home.footer.legalHeading')}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button className="nav-link" style={{ textAlign: 'left' }} onClick={() => navigate('/privacy')}>{t('home.footer.privacyLink')}</button>
                  <button className="nav-link" style={{ textAlign: 'left' }} onClick={() => navigate('/terms')}>{t('home.footer.termsLink')}</button>
                  <button className="nav-link" style={{ textAlign: 'left' }} onClick={() => navigate('/disclaimer')}>{t('home.footer.disclaimerLink')}</button>
                </div>
              </div>
            </div>

            <div className="divider" style={{ marginBottom: 24 }} />

            <p style={{ fontSize: 11.5, color: '#334155', lineHeight: 1.7, marginBottom: 20, maxWidth: 760 }}>
              {t('home.footer.disclaimer')}
            </p>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <p style={{ fontSize: 11.5, color: '#334155' }}>
                {t('home.footer.copyright')}
              </p>
              <a href={SURVEY_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#475569', textDecoration: 'none' }}>
                {t('home.footer.feedbackLink')} <ExternalLink size={11} />
              </a>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default HomePage;
